import { useEffect, useState } from 'react';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { maskCPF, maskPhone } from '../../../shared/utils/validators';
import { Collaborator, UpdateCollaboratorPayload, mensagemDeErro } from '../api';
import { useAtualizarColaborador } from '../hooks';
import { PersonalDataFields } from './PersonalDataFields';
import { PessoasModal } from './PessoasModal';

interface Props {
  open: boolean;
  person: Collaborator;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const PERSONAL_KEYS = [
  'socialName', 'preferredName', 'birthDate', 'birthCountry', 'nationality', 'birthCityId', 'sex',
  'maritalStatus', 'motherName', 'fatherName', 'educationLevel',
] as const;

/**
 * Edição dos dados pessoais. O CPF só é editável quando chega completo
 * (`pessoas.dados-sensiveis`); nome e CPF exigem justificativa
 * (RN-GP-001-054).
 */
export function EditPersonModal({ open, person, onClose, onSaved }: Props) {
  const salvar = useAtualizarColaborador(person.id);
  const [form, setForm] = useState<UpdateCollaboratorPayload>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm({
      name: person.name,
      cpf: person.cpfMasked ? undefined : person.cpf,
      rg: person.rg,
      email: person.email,
      phone: person.phone,
      socialName: person.socialName,
      preferredName: person.preferredName,
      birthDate: person.birthDate,
      birthCountry: person.birthCountry,
      nationality: person.nationality,
      birthCityId: person.birthCity?.id ?? null,
      sex: person.sex,
      maritalStatus: person.maritalStatus,
      motherName: person.motherName,
      fatherName: person.fatherName,
      educationLevel: person.educationLevel,
    });
  }, [open, person]);

  const nameChanged = form.name !== undefined && form.name.trim() !== person.name;
  const cpfChanged = !person.cpfMasked && form.cpf !== undefined && form.cpf.replace(/\D/g, '') !== person.cpf.replace(/\D/g, '');
  const precisaJustificar = nameChanged || cpfChanged;

  async function submit() {
    if (precisaJustificar && !form.justification?.trim()) {
      return setError('Alterar nome completo ou CPF exige justificativa.');
    }
    const payload: UpdateCollaboratorPayload = {
      name: form.name?.trim(),
      rg: form.rg?.trim(),
      email: form.email?.trim(),
      phone: form.phone?.trim(),
    };
    if (cpfChanged) payload.cpf = form.cpf;
    if (precisaJustificar) payload.justification = form.justification?.trim();
    for (const key of PERSONAL_KEYS) {
      const v = form[key];
      (payload as Record<string, unknown>)[key] = typeof v === 'string' ? v.trim() || null : v ?? null;
    }
    try {
      await salvar.mutateAsync(payload);
      onSaved('Dados pessoais atualizados.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar os dados pessoais.'));
    }
  }

  return (
    <PessoasModal
      open={open}
      size="lg"
      title="Editar dados pessoais"
      submitLabel="Salvar"
      isSubmitting={salvar.isPending}
      submitError={error}
      onClose={onClose}
      onSubmit={submit}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput label="Nome completo" value={form.name ?? ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <TextInput
          label="CPF"
          value={person.cpfMasked ? person.cpf : form.cpf ?? ''}
          disabled={person.cpfMasked}
          onChange={(e) => setForm((f) => ({ ...f, cpf: maskCPF(e.target.value) }))}
        />
        <TextInput label="RG" value={form.rg ?? ''} onChange={(e) => setForm((f) => ({ ...f, rg: e.target.value }))} />
        <TextInput label="E-mail" type="email" value={form.email ?? ''} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <TextInput label="Telefone" value={form.phone ?? ''} onChange={(e) => setForm((f) => ({ ...f, phone: maskPhone(e.target.value) }))} />
      </div>
      <PersonalDataFields
        value={form}
        initialUf={person.birthCity?.uf}
        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
      />
      {precisaJustificar && (
        <Textarea
          label="Justificativa da alteração de nome ou CPF"
          value={form.justification ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, justification: e.target.value }))}
        />
      )}
    </PessoasModal>
  );
}
